import {
  Directive,
  ElementRef,
  inject,
  OnInit,
} from '@angular/core';

import { TW_DROPDOWN_CONTEXT } from './tw-dropdown-context';

/**
 * `button[twDropdownTrigger]` — attribute directive applied to a
 * real `<button>` that opens the dropdown menu (TWS-122 Wave 1,
 * fixed under the same ticket after the root-cause analysis
 * showed the custom-element selector was silently dropping
 * `popovertarget`).
 *
 * ### Why an attribute directive on `<button>`?
 *
 * The native `[popover]` invoker semantics (`popovertarget`) are
 * **button-specific** per the HTML spec — only `<button>` and
 * `<input type="button|submit|reset|image">` have a
 * `popoverTargetElement` IDL property. On any other element
 * (including the previous `<tw-dropdown-trigger>` custom element)
 * `popovertarget` is silently ignored and the menu never opens.
 *
 * Hosting the directive on a real `<button>` restores:
 *   - Native click / Enter / Space toggling.
 *   - Light-dismiss and top-layer semantics.
 *   - Escape to close without any JS handlers.
 *   - `aria-expanded` / `aria-haspopup` on the actual button the
 *     screen reader announces.
 *
 * ### Keyboard
 *
 * `ArrowDown` opens the menu AND focuses the first item (WAI-ARIA
 * menu pattern). Other keys flow through to the native button
 * behaviour (Space / Enter fire a click which the native
 * `popovertarget` translates to "toggle the popover").
 *
 * ### CSS anchor wiring
 *
 * The host sets `anchor-name: var(<context-provided name>)` via an
 * inline style on init. That names this element as the anchor;
 * the menu's `position-anchor` declaration references the same
 * name to pin itself relative to this button.
 */
@Directive({
  selector: 'button[twDropdownTrigger]',
  standalone: true,
  host: {
    'class': 'tw-dropdown__trigger',
    'type': 'button',
    '[attr.popovertarget]': 'ctx.popoverId',
    '[attr.aria-haspopup]': '"menu"',
    '[attr.aria-expanded]': 'ctx.open()',
    '(keydown.arrowdown)': 'onArrowDown($event)',
  },
})
export class TwDropdownTriggerDirective implements OnInit {
  protected readonly ctx = inject(TW_DROPDOWN_CONTEXT);
  private readonly host = inject<ElementRef<HTMLButtonElement>>(ElementRef);

  /**
   * Intentionally wires on init rather than in a constructor-level
   * effect. We only need to:
   *   1. Register the host element with the context (for focus
   *      return on close);
   *   2. Write the `anchor-name` inline style once — it's a
   *      static CSS custom property; no signal-driven updates.
   */
  ngOnInit(): void {
    const el = this.host.nativeElement;
    this.ctx.registerTriggerElement(el);
    // The CSS `anchor-name` property takes a dashed-ident directly
    // (`--my-anchor`), NOT a `var()` wrapper. The parent factory
    // hands us a ready-to-use name like `--twist-anchor-7`; we
    // write it as the value of the `anchor-name` property on the
    // trigger's host. The menu then uses the same name for its
    // `position-anchor` declaration.
    el.style.setProperty('anchor-name', this.ctx.anchorName);
  }

  /**
   * Native `popovertarget` handles click / Enter / Space naturally.
   * `ArrowDown` is NOT a native open trigger — we add it explicitly
   * so keyboard users can open the menu and land on the first item
   * in one keystroke (WAI-ARIA menu pattern).
   *
   * `preventDefault` on the original event stops the browser from
   * also scrolling the page.
   */
  protected onArrowDown(event: Event): void {
    event.preventDefault();
    this.ctx.requestOpenAndFocusFirst();
  }
}

/**
 * Back-compat alias. The directive originally shipped as
 * `TwDropdownTriggerComponent` (element selector
 * `<tw-dropdown-trigger>`), but that selector was silently
 * dropping `popovertarget` on the custom-element host — see the
 * directive class docstring for the spec rationale. Consumers
 * migrating from the old name keep a working import; the symbol
 * is the same class.
 *
 * @deprecated Use `TwDropdownTriggerDirective` and host on a real
 * `<button>` — `<button twDropdownTrigger>…</button>`.
 */
export const TwDropdownTriggerComponent = TwDropdownTriggerDirective;
